import React, { useEffect, useState } from "react";
import EloBadge from "../common/EloBadge.js";
import PropTypes from "prop-types";
import { Link, useHistory, useParams } from "react-router-dom";
import { withStyles } from "@mui/styles";

import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FlagIcon from "@mui/icons-material/Flag";
import PersonIcon from "@mui/icons-material/Person";
import MapIcon from "@mui/icons-material/Map";
import EventIcon from "@mui/icons-material/Event";
import UpdateIcon from "@mui/icons-material/Update";
import TimelineIcon from "@mui/icons-material/Timeline";

import { formatMapName } from "../../constants/maps";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    paddingTop: "2.5rem",
    backgroundColor: "#0b0b0b",
  },
  container: {
    width: "90%",
    maxWidth: "1200px",
    margin: "1.5rem auto",
    [theme.breakpoints.up("xl")]: {
      margin: "2rem auto",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: "2rem",
    gap: "1rem",
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#ffffff",
    flex: 1,
    [theme.breakpoints.up("xl")]: {
      fontSize: "2.2rem",
    },
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.875rem",
    padding: "0.5rem 1rem",
  },
  statusSubmitted: {
    backgroundColor: "rgba(59, 130, 246, 0.2)",
    color: "#60a5fa",
  },
  statusInReview: {
    backgroundColor: "rgba(251, 191, 36, 0.2)",
    color: "#fbbf24",
  },
  statusResolved: {
    backgroundColor: "rgba(34, 197, 94, 0.2)",
    color: "#4ade80",
  },
  statusConcluded: {
    backgroundColor: "rgba(168, 85, 247, 0.2)",
    color: "#a855f7",
  },
  metaRow: {
    display: "flex",
    gap: "2rem",
    marginBottom: "2rem",
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "0.95rem",
    alignItems: "center",
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  detailsCard: {
    backgroundColor: "#1C1E20",
    borderRadius: "12px",
    padding: "2rem",
    marginBottom: "2rem",
    border: "1px solid rgba(232, 163, 61, 0.2)",
  },
  sectionTitle: {
    fontSize: "1.3rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: "1.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  matchInfo: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    marginBottom: "2rem",
    padding: "1rem",
    backgroundColor: "rgba(232, 163, 61, 0.1)",
    borderRadius: "8px",
  },
  playersRow: {
    display: "flex",
    gap: "2rem",
    marginBottom: "2rem",
    justifyContent: "space-around",
  },
  playerCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "1.5rem",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "12px",
  },
  avatar: {
    width: "80px",
    height: "80px",
    marginBottom: "1rem",
    border: "2px solid rgba(232, 163, 61, 0.5)",
  },
  playerName: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: "0.5rem",
  },
  playerSteamId: {
    fontSize: "0.75rem",
    color: "rgba(255, 255, 255, 0.5)",
    fontFamily: "monospace",
  },
  roleLabel: {
    fontSize: "0.7rem",
    padding: "0.25rem 0.75rem",
    borderRadius: "12px",
    fontWeight: 600,
    marginTop: "0.5rem",
  },
  reporter: {
    backgroundColor: "rgba(34, 197, 94, 0.2)",
    color: "#4ade80",
  },
  reported: {
    backgroundColor: "rgba(239, 68, 68, 0.2)",
    color: "#f87171",
  },
  offensesContainer: {
    marginBottom: "1.5rem",
  },
  offensesList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
  },
  offenseChip: {
    backgroundColor: "rgba(239, 68, 68, 0.2)",
    color: "#f87171",
    fontWeight: 600,
  },
  remarksContainer: {
    marginBottom: "1.5rem",
  },
  remarksText: {
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: "1rem",
    lineHeight: 1.6,
    fontStyle: "italic",
    padding: "1rem",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "8px",
    borderLeft: "4px solid #E8A33D",
  },
  timeline: {
    marginTop: "1rem",
  },
  timelineItem: {
    display: "flex",
    gap: "1rem",
    marginBottom: "1.5rem",
    position: "relative",
    paddingLeft: "2rem",
    "&:before": {
      content: '""',
      position: "absolute",
      left: "0.5rem",
      top: "1.5rem",
      bottom: "-1.5rem",
      width: "2px",
      backgroundColor: "rgba(232, 163, 61, 0.3)",
    },
    "&:last-child:before": {
      display: "none",
    },
  },
  timelineDot: {
    position: "absolute",
    left: 0,
    top: "0.25rem",
    width: "1rem",
    height: "1rem",
    borderRadius: "50%",
    backgroundColor: "#E8A33D",
    border: "2px solid #0b0b0b",
  },
  timelineContent: {
    flex: 1,
  },
  timelineDate: {
    fontSize: "0.8rem",
    color: "rgba(255, 255, 255, 0.5)",
    marginBottom: "0.25rem",
  },
  timelineTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: "0.25rem",
  },
  timelineDescription: {
    fontSize: "0.9rem",
    color: "rgba(255, 255, 255, 0.7)",
    lineHeight: 1.5,
  },
  timelineAdmin: {
    fontSize: "0.8rem",
    color: "#E8A33D",
    fontWeight: 500,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "4rem",
  },
  error: {
    textAlign: "center",
    padding: "4rem 2rem",
    color: "#f87171",
  },
});

function ReportDetailPage({ classes, actions, catalog }) {
  const history = useHistory();
  const { reportId } = useParams();
  const { user } = catalog || {};

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && reportId) {
      fetchReport();
    }
  }, [user, reportId]);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${basePath}/api/v1/reports/${reportId}`, {
        credentials: "include",
      });

      const data = await response.json();

      if (data.ok) {
        console.log("🔍 Report data received:", data.report);
        console.log("🖼️ Reporter picture URL:", data.report.reporterPicture);
        console.log("🖼️ Reported picture URL:", data.report.reportedPicture);
        setReport(data.report);
      } else {
        setError(data.message || "Failed to load report");
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setError("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "submitted":
        return classes.statusSubmitted;
      case "in_review":
        return classes.statusInReview;
      case "resolved":
        return classes.statusResolved;
      case "concluded":
        return classes.statusConcluded;
      default:
        return classes.statusSubmitted;
    }
  };

  const formatStatusLabel = (status) => {
    return status.replace(/_/g, " ").toUpperCase();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const formatOffenseName = (offense) => {
    return offense.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.container}>
          <div className={classes.loading}>
            <CircularProgress style={{ color: "#E8A33D" }} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className={classes.root}>
        <div className={classes.container}>
          <div className={classes.error}>
            <Typography variant="h5">{error || "Report not found"}</Typography>
            <Button
              onClick={() => history.push("/reports")}
              style={{ marginTop: "1rem", color: "#E8A33D" }}
            >
              Back to Reports
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const offenses = Object.keys(report.offenses).filter((k) => report.offenses[k]);

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <div className={classes.header}>
          <IconButton
            onClick={() => history.push("/reports")}
            style={{ color: "#E8A33D" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className={classes.title}>
            Report #{reportId.slice(-6)}
          </Typography>
          <Chip
            label={formatStatusLabel(report.status)}
            className={`${classes.statusChip} ${getStatusClass(report.status)}`}
          />
        </div>

        <div className={classes.metaRow}>
          <div className={classes.metaItem}>
            <EventIcon fontSize="small" />
            <Typography variant="body2">Submitted: {formatDate(report.createdAt)}</Typography>
          </div>
          <div className={classes.metaItem}>
            <UpdateIcon fontSize="small" />
            <Typography variant="body2">Updated: {formatDate(report.lastUpdatedAt)}</Typography>
          </div>
        </div>

        <Paper className={classes.detailsCard} elevation={0}>
          <Typography className={classes.sectionTitle}>
            <FlagIcon />
            Report Details
          </Typography>

          <div className={classes.matchInfo}>
            <MapIcon style={{ color: "#E8A33D" }} />
            <Typography style={{ color: "#ffffff", flex: 1 }}>
              <strong>Map:</strong> {report.matchMap ? formatMapName(report.matchMap) : "Unknown"}
            </Typography>
            <Button
              component={Link}
              to={`/match/${report.matchId}`}
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon />}
              style={{
                color: "#E8A33D",
                borderColor: "#E8A33D",
              }}
            >
              View Match
            </Button>
          </div>

          <div className={classes.playersRow}>
            <div className={classes.playerCard}>
              <Avatar
                src={report.reporterPicture || undefined}
                alt={report.reporterNickname}
                className={classes.avatar}
                onError={(e) => {
                  console.error("❌ Failed to load reporter picture:", report.reporterPicture);
                  e.target.src = "";
                }}
              >
                {!report.reporterPicture && <PersonIcon style={{ fontSize: "3rem" }} />}
              </Avatar>
              <Typography className={classes.playerName}>
                {report.reporterNickname}
              </Typography>
              <EloBadge value={report.reporterElo} size="small" />
              <Typography className={classes.playerSteamId}>
                {report.reporterSteamId}
              </Typography>
              <Chip label="REPORTER" className={`${classes.roleLabel} ${classes.reporter}`} />
            </div>

            <div style={{ display: "flex", alignItems: "center", color: "#E8A33D", fontSize: "2rem" }}>
              →
            </div>

            <div className={classes.playerCard}>
              <Avatar
                src={report.reportedPicture || undefined}
                alt={report.reportedNickname}
                className={classes.avatar}
                onError={(e) => {
                  console.error("❌ Failed to load reported picture:", report.reportedPicture);
                  e.target.src = "";
                }}
              >
                {!report.reportedPicture && <PersonIcon style={{ fontSize: "3rem" }} />}
              </Avatar>
              <Typography className={classes.playerName}>
                {report.reportedNickname}
              </Typography>
              <EloBadge value={report.reportedElo} size="small" />
              <Typography className={classes.playerSteamId}>
                {report.reportedSteamId}
              </Typography>
              <Chip label="REPORTED" className={`${classes.roleLabel} ${classes.reported}`} />
            </div>
          </div>

          <div className={classes.offensesContainer}>
            <Typography style={{ color: "#ffffff", fontWeight: 600, marginBottom: "0.75rem" }}>
              Reported Offenses:
            </Typography>
            <div className={classes.offensesList}>
              {offenses.map((offense) => (
                <Chip
                  key={offense}
                  label={formatOffenseName(offense)}
                  className={classes.offenseChip}
                />
              ))}
            </div>
          </div>

          {report.remarks && (
            <div className={classes.remarksContainer}>
              <Typography style={{ color: "#ffffff", fontWeight: 600, marginBottom: "0.75rem" }}>
                Reporter's Description:
              </Typography>
              <Typography className={classes.remarksText}>
                "{report.remarks}"
              </Typography>
            </div>
          )}
        </Paper>

        <Paper className={classes.detailsCard} elevation={0}>
          <Typography className={classes.sectionTitle}>
            <TimelineIcon />
            Report Timeline
          </Typography>

          <div className={classes.timeline}>
            {report.timeline && report.timeline.length > 0 ? (
              report.timeline.map((item, index) => (
                <div key={index} className={classes.timelineItem}>
                  <div className={classes.timelineDot} />
                  <div className={classes.timelineContent}>
                    <Typography className={classes.timelineDate}>
                      {formatDate(item.timestamp)}
                    </Typography>
                    {item.type === "created" && (
                      <>
                        <Typography className={classes.timelineTitle}>
                          Report Created
                        </Typography>
                        <Typography className={classes.timelineDescription}>
                          {item.message}
                        </Typography>
                      </>
                    )}
                    {item.type === "status_change" && (
                      <>
                        <Typography className={classes.timelineTitle}>
                          Status Update
                        </Typography>
                        <Typography className={classes.timelineDescription}>
                          {item.message}
                        </Typography>
                        {item.adminName && (
                          <Typography className={classes.timelineAdmin}>
                            By: {item.adminName}
                          </Typography>
                        )}
                      </>
                    )}
                    {item.type === "admin_note" && (
                      <>
                        <Typography className={classes.timelineTitle}>
                          Admin Note
                        </Typography>
                        <Typography className={classes.timelineDescription}>
                          {item.content}
                        </Typography>
                        {item.adminName && (
                          <Typography className={classes.timelineAdmin}>
                            By: {item.adminName}
                          </Typography>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <Typography style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                No timeline updates yet
              </Typography>
            )}
          </div>
        </Paper>
      </div>
    </div>
  );
}

ReportDetailPage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
};

export default withStyles(styles)(ReportDetailPage);
